import { useEffect, useState, type ReactNode } from 'react';
import { AxiosError } from 'axios';

import { getMe, logout as apiLogout, switchOrg as apiSwitchOrg } from '@/api/auth.api';
import { AuthContext, type AuthState } from './AuthContextValue';

/**
 * Hydrates the session from the kw_session cookie on mount via /api/auth/me.
 * Treats 401 as "unauthenticated" (no error). Anything else logs and
 * unauthenticates so the user can retry by reloading.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = async (): Promise<void> => {
    try {
      const me = await getMe();
      setState({ status: 'authenticated', me });
    } catch (err) {
      // 401 / 403 are the expected "no session" paths — not a bug to surface.
      if (err instanceof AxiosError && (err.response?.status === 401 || err.response?.status === 403)) {
        setState({ status: 'unauthenticated' });
        return;
      }
      // Anything else (network error, 5xx) — log and treat as unauth so the
      // user is shown the login page rather than a broken loading spinner.
      console.error('AuthContext.refresh failed:', err);
      setState({ status: 'unauthenticated' });
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await apiLogout();
    } catch (err) {
      // Even if logout fails server-side, drop the local session — user
      // intent is "I'm done". Cookie may linger but next request returns 401.
      console.error('AuthContext.signOut failed:', err);
    }
    setState({ status: 'unauthenticated' });
  };

  const switchOrg = async (organizationId: string): Promise<void> => {
    // The API call re-mints the kw_session cookie for the new org; the
    // refresh re-hydrates user/membership/org from it. Errors propagate to
    // the caller (e.g. the OrgSwitcher) so it can surface them.
    await apiSwitchOrg(organizationId);
    await refresh();
  };

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <AuthContext.Provider value={{ state, refresh, signOut, switchOrg }}>{children}</AuthContext.Provider>
  );
}

// Re-export for callers — keeps imports concise (`import { AuthProvider } …`).
export { AuthContext } from './AuthContextValue';
export type { AuthState, AuthContextValue } from './AuthContextValue';
